/**
 * `use-active-org` — piece 2 of MULTI_ORG_SWITCHER: exposes `orgs()`,
 * `activeOrg()`/`activeOrgId()` and `setActiveOrg()`. The active org derives
 * from the VALIDATED module pointer (`lib/active-org.ts`), so a switch
 * reactively re-scopes every instance of the hook without a reload.
 *
 * Isolation (piece 5): `assertBelongsToActiveOrg` discards a deep-linked
 * entity id that does NOT belong to the active org after a switch —
 * prevents operating in org B with an entity dragged over from org A.
 */

import { createMemo } from "solid-js";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { handleApiResponse } from "@space/shared";
import { activeOrgEpoch, resolveActiveOrgId, setPreferredOrgId } from "@/lib/active-org";
import { useMyOrganizations } from "@/features/courses/hooks/use-course-data";

export interface ActiveOrgApi {
  /** User's orgs (synced local-first). */
  orgs: () => { id: string; name: string | null; slug: string | null }[];
  /** Validated active org id (or undefined if there are no orgs). */
  activeOrgId: () => string | undefined;
  /** Active org's row (or undefined). */
  activeOrg: () => { id: string; name: string | null; slug: string | null } | undefined;
  /** Is the org list loading? */
  isLoading: () => boolean;
  /** Changes the active org (EXPLICIT switch): persists local + server session. */
  setActiveOrg: (orgId: string) => Promise<void>;
  /** Epoch of the last explicit switch (for isolation guards). */
  epoch: () => number;
}

export function useActiveOrg(): ActiveOrgApi {
  const { userId } = useSession();
  const query = useMyOrganizations();

  const orgs = () =>
    (query.data ?? []).map((o) => ({
      id: o.id,
      name: o.name ?? null,
      slug: null as string | null,
    }));

  const activeOrgId = createMemo(() =>
    resolveActiveOrgId(
      userId(),
      orgs().map((o) => o.id),
    ),
  );

  const activeOrg = () => orgs().find((o) => o.id === activeOrgId());

  const setActiveOrg = async (orgId: string) => {
    const uid = userId();
    if (!uid) {
      return;
    }
    // 1) reactive local pointer (re-scopes everything instantly).
    setPreferredOrgId(uid, orgId, true);
    // 2) server session (CC-30: wraps the response to throw on non-2xx).
    await handleApiResponse(
      api.POST("/api/org/setActiveOrganization", { body: { organizationId: orgId } }),
    );
  };

  return {
    orgs,
    activeOrgId,
    activeOrg,
    isLoading: () => query.isLoading,
    setActiveOrg,
    epoch: activeOrgEpoch,
  };
}

/**
 * Isolation guard (piece 5): returns `true` if `entityOrgId` matches the
 * active org. Use it to discard deep-linked entities from ANOTHER org after
 * a switch (don't show/operate on org A's data while in org B).
 */
export function belongsToActiveOrg(
  entityOrgId: string | undefined,
  activeOrgId: string | undefined,
): boolean {
  if (!entityOrgId || !activeOrgId) {
    return false;
  }
  return entityOrgId === activeOrgId;
}
